'use client'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

import { gql, useMutation, useQuery } from '@apollo/client'
import {
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'

import LanguageSwitcher from '@/components/shared/languageSelect'
import { profile } from 'console'

import { useLanguage } from '@/context/LanguageContext'
import { useDemoMode } from '@/context/DemoModeContext'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { get } from 'http'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import '@/components/styles/Form.css' // Import the CSS file
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'

import PaletteSelect from '@/components/shared/inputComponents/PaletteSelect'

import AdditionalComponentConstraints from '@/components/AdditionalComponentConstraints'
import {
  CREATE_B2B_CUSTOMER,
  DELETE_SINGLE_B2B_CUSTOMER,
  GET_B2B_CUSTOMER,
  UPDATE_B2B_CUSTOMER,
} from './B2BCustomersQueries'
import useImpersonateB2BCustomer from '@/hooks/useImpersonateB2BCustomer'
import { DataTableManual } from '@/components/shared/data-table-manual/data-table-manual'
import { Separator } from '@/components/ui/separator'
import TagsSelect from '@/components/shared/inputComponents/TagsSelect'
import CustomerGroupSelect from '@/components/shared/inputComponents/CustomerGroupSelect'
import PriceListSelect from '@/components/shared/inputComponents/PriceListSelect'
import ManagerSelect from '@/components/shared/inputComponents/ManagerSelect'
import { UnifiedCustomerAccountTable } from '@/containers/CustomerAccounts/UnifiedCustomerAccountTable'
import { Trash2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

const formSchema = z
  .object({
    customer_groupId: z.number().nullable(),
    price_listId: z.number().nullable(),
    managers: z.array(z.number()).optional(),
    fabric_palettes: z.array(z.number()).optional(),
    b2b_customer_discount: z.preprocess(
      (val) => {
        // Handle empty string, null, or undefined
        if (val === '' || val === null || val === undefined) return undefined
        // Convert string to number
        const num = Number(val)
        return isNaN(num) ? undefined : num
      },
      z.number().min(0).max(99).optional()
    ),
    b2b_company_name: z.string().optional().nullable(),
    b2b_company_code: z.string().optional().nullable(),
    b2b_company_vat_code: z.string().optional().nullable(),
    b2b_company_website: z.string().optional().nullable(),
    additional_components: z
      .record(z.array(z.object({ id: z.number() })))
      .optional(),
    name: z.string().optional().nullable(),
    email: z.string().optional().nullable(),
    is_b2b_user: z.boolean().default(true),
    phone_number: z.string().optional().nullable(),
    country: z.string().optional().nullable(),
    city: z.string().optional().nullable(),
    postal_code: z.string().optional().nullable(),
    address_1: z.string().optional().nullable(),
    b2b_only_selected_products: z.boolean().default(false),
    selected_products: z.array(z.number()).optional(),
    account_code: z.string().min(1, 'Account code is required'),
    tags: z.array(z.number()).optional(),
    is_configurator_enabled: z.boolean().default(false),
    is_claims_enabled: z.boolean().default(false),
  })
  .refine(
    data => {
      const hasName = data.name && data.name.trim().length > 0
      const hasCompanyName =
        data.b2b_company_name && data.b2b_company_name.trim().length > 0
      return hasName || hasCompanyName
    },
    {
      message: 'Either Name or Company Name is required',
      path: ['name'], // Show error on name field
    }
  )

interface MyFormProps {
  data: {
    id: number
    // Add other properties of data as needed
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void // Add this prop
}

export default function B2BCustomerForm({
  data,
  onDirtyChange,
  onRequestClose,
}: MyFormProps) {
  const { isDemoMode } = useDemoMode()
  const [id, setId] = useState(data?.id) // Introduce id state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const {
    data: { findUniqueCustomer: customerData } = {},
    loading: loadingData,
    error,
  } = useQuery(GET_B2B_CUSTOMER, {
    variables: {
      id: id, // Use id instead of data?.id
    },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      customer_groupId: null,
      price_listId: null,
      managers: [],
      fabric_palettes: [],
      b2b_customer_discount: undefined,
      b2b_company_name: '',
      b2b_company_code: '',
      b2b_company_vat_code: '',
      b2b_company_website: '',
      additional_components: {}, // Initialize additional_components
      name: '',
      email: '',
      is_b2b_user: true,
      phone_number: '',
      country: '',
      city: '',
      postal_code: '',
      address_1: '',
      b2b_only_selected_products: false,
      selected_products: [],
      account_code: '',
      tags: [],
      is_configurator_enabled: false,
      is_claims_enabled: false,
    },
  })

  console.log('form', form.formState.errors)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = form

  const handleGraphQLError = (error: any, operation: 'create' | 'update') => {
    // Check for GraphQL errors
    const graphQLError = error.graphQLErrors?.[0]

    if (graphQLError?.message) {
      // Check for account code conflict error
      if (
        graphQLError.message.includes('account code') &&
        graphQLError.message.includes('already exists')
      ) {
        form.setError('account_code', {
          type: 'server',
          message: 'Customer with this account code already exists',
        })
        return // Don't show toast for this specific error
      }
    }

    // Show generic toast for other errors
    console.error(
      `Error ${operation === 'create' ? 'creating' : 'updating'} customer:`,
      error
    )
    toast.error(`Failed to ${operation} customer`)
  }

  const [updateCustomerGroup, { loading: updateLoading }] = useMutation(
    UPDATE_B2B_CUSTOMER,
    {
      onError: e => {
        handleGraphQLError(e, 'update')
      },
      onCompleted: () => {
        toast.success('Customer updated successfully')
        reset() // Reset form to clear dirty state
      },
      refetchQueries: ['GET_B2B_CUSTOMERS'],
    }
  )

  const [createCustomerGroup, { loading: createLoading }] = useMutation(
    CREATE_B2B_CUSTOMER,
    {
      onError: e => {
        handleGraphQLError(e, 'create')
      },
      onCompleted: response => {
        // Capture new ID
        toast.success('Customer created successfully')
        console.log('Customer created:', response)
        const newId = response.createB2Bcustomer.id
        // Adjust according to actual response structure
        setId(newId) // Set the new id
        reset() // Optionally reset the form
      },
      refetchQueries: ['GET_B2B_CUSTOMERS'],
    }
  )

  const [deleteCustomer, { loading: deleteLoading }] = useMutation(
    DELETE_SINGLE_B2B_CUSTOMER,
    {
      onError: e => {
        console.error('Error deleting customer:', e)
        toast.error('Failed to delete customer')
      },
      onCompleted: () => {
        toast.success('Customer deleted successfully')
        setShowDeleteDialog(false)
        onRequestClose?.()
      },
      refetchQueries: ['GET_B2B_CUSTOMERS'],
    }
  )

  const handleDelete = () => {
    if (id) {
      deleteCustomer({ variables: { id } })
    }
  }

  function onSubmit(values: z.infer<typeof formSchema>) {
    console.log('values', values)
    if (id) {
      // Use id to determine update
      updateCustomerGroup({
        variables: {
          id: id, // Use id
          b2b_customer_discount: Number(values.b2b_customer_discount),
          b2b_company_name: values.b2b_company_name,
          b2b_company_code: values.b2b_company_code,
          b2b_company_vat_code: values.b2b_company_vat_code,
          b2b_company_website: values.b2b_company_website,
          customer_groupId: values.customer_groupId,
          price_listId: values.price_listId,
          managers: values.managers,
          fabric_palettes: values.fabric_palettes,
          additional_components: values.additional_components
            ? Object.values(values.additional_components)
                .flat()
                .map((item: any) => item.id)
            : [],
          name: values.name,
          email: values.email,
          is_b2b_user: values.is_b2b_user,
          phone_number: values.phone_number,
          country: values.country,
          city: values.city,
          postal_code: values.postal_code,
          address_1: values.address_1,
          b2b_only_selected_products: values.b2b_only_selected_products,
          selected_products: [], //values.selected_products,
          account_code: values.account_code,
          tags: values.tags?.map((tag: any) => ({
            id: tag,
          })),
          is_configurator_enabled: values.is_configurator_enabled,
          is_claims_enabled: values.is_claims_enabled,
          language: 'en',
        },
      })
    } else {
      createCustomerGroup({
        variables: {
          b2b_customer_discount: Number(values.b2b_customer_discount),
          b2b_company_name: values.b2b_company_name,
          b2b_company_code: values.b2b_company_code,
          b2b_company_vat_code: values.b2b_company_vat_code,
          b2b_company_website: values.b2b_company_website,
          customer_groupId: values.customer_groupId,
          price_listId: values.price_listId,
          managers: values.managers?.map(managerId => ({
            id: managerId,
          })),
          fabric_palettes: values.fabric_palettes,
          additional_components: values.additional_components
            ? Object.values(values.additional_components)
                .flat()
                .map((item: any) => item.id)
            : [],
          name: values.name,
          email: values.email,
          is_b2b_user: values.is_b2b_user,
          phone_number: values.phone_number,
          country: values.country,
          city: values.city,
          postal_code: values.postal_code,
          address_1: values.address_1,
          b2b_only_selected_products: values.b2b_only_selected_products,
          selected_products: [], // values.selected_products,
          account_code: values.account_code,
          tags: values.tags?.map((tag: any) => ({
            id: tag,
          })),
          is_configurator_enabled: values.is_configurator_enabled,
          is_claims_enabled: values.is_claims_enabled,
          language: 'en',
        },
      })
    }
  }

  const {
    impersonate,
    loading: impersonateLoading,
    error: impersonateError,
    data: impersonateData,
  } = useImpersonateB2BCustomer()

  useEffect(() => {
    if (customerData) {
      // Convert array to record format expected by the component
      const additionalComponents: Record<string, { id: number }[]> = {}
      customerData.additional_components.forEach((item: any) => {
        const groupId = item.additionalComponent.additionalComponentGroupId
        if (!additionalComponents[groupId]) {
          additionalComponents[groupId] = []
        }
        additionalComponents[groupId].push({ id: item.additionalComponent.id })
      })

      console.log('customerData', customerData)

      reset({
        customer_groupId: customerData.customer_group?.id || null,
        price_listId: customerData.price_listId || null,
        managers: customerData.managers?.map((m: any) => m.managerId) || [],
        fabric_palettes:
          customerData.fabric_palettes?.map((palette: any) => palette.id) || [],
        b2b_customer_discount: customerData.b2b_customer_discount,
        b2b_company_name: customerData.b2b_company_name,
        b2b_company_code: customerData.b2b_company_code,
        b2b_company_vat_code: customerData.b2b_company_vat_code,
        b2b_company_website: customerData.b2b_company_website,
        additional_components: additionalComponents,
        name: customerData.name,
        email: customerData.email,
        is_b2b_user: true,
        phone_number: customerData.phone_number || '',
        country: customerData.country || '',
        city: customerData.city || '',
        postal_code: customerData.postal_code || '',
        address_1: customerData.address_1 || '',
        b2b_only_selected_products: false,
        selected_products: customerData.selected_products.map(
          (product: any) => product.product_containerId
        ),
        account_code: customerData.account_code || '',
        tags: customerData.tags.map((tag: any) => tag.id),
        is_configurator_enabled: customerData.is_configurator_enabled || false,
        is_claims_enabled: customerData.is_claims_enabled || false,
      })
    }
  }, [customerData, reset])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  return (
    <>
      <SheetContent className="sheet-content flex flex-col">
        <SheetHeader className="flex-shrink-0">
          <div className="flex justify-between items-center">
            <SheetTitle>B2B Customer</SheetTitle>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                onClick={() => {
                  // Add your login logic here
                  impersonate(id)
                }}
              >
                Impersonate
              </Button>
              <LanguageSwitcher />
            </div>
          </div>
          <SheetDescription>ID: {id}</SheetDescription>
        </SheetHeader>
        <SheetScrollableContent>
          <Form {...form}>
            {loadingData ? (
              <div className="grid grid-cols-4 items-start gap-4 py-5">
                {[...Array(4)].map((_, i) => (
                  <>
                    <div className="space-y-2 col-span-1">
                      <Skeleton className="h-6 w-[100px]" />
                    </div>
                    <div className="space-y-2 col-span-3">
                      <Skeleton className="h-10 w-full" />
                    </div>
                  </>
                ))}
              </div>
            ) : (
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="space-y-4 max-w-3xl mx-auto py-5"
              >
                <div className="grid gap-4 py-4">
                  <InputFieldContainer
                    label="Discount"
                    control={control}
                    name="b2b_customer_discount"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            onChange={e => {
                              const numericValue = e.target.value.replace(
                                /\D/g,
                                ''
                              )
                              field.onChange(numericValue)
                            }}
                            value={field.value || ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <InputFieldContainer
                    label="Account Code"
                    control={control}
                    name="account_code"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Input
                            placeholder="Account Code"
                            type="text"
                            {...field}
                            className={isDemoMode ? 'blur-sm select-none' : ''}
                            readOnly={isDemoMode}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <InputFieldContainer
                    label="Managers"
                    control={control}
                    name="managers"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <div className={isDemoMode ? 'blur-sm select-none pointer-events-none' : ''}>
                            <ManagerSelect field={field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Separator />
                  <InputFieldContainer
                    label="Name"
                    control={control}
                    name="name"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Input
                            placeholder="Name"
                            type="text"
                            {...field}
                            className={isDemoMode ? 'blur-sm select-none' : ''}
                            readOnly={isDemoMode}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <InputFieldContainer
                    label="Company Name"
                    control={control}
                    name="b2b_company_name"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Input
                            placeholder="Company Name"
                            type="text"
                            {...field}
                            className={isDemoMode ? 'blur-sm select-none' : ''}
                            readOnly={isDemoMode}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <InputFieldContainer
                    label="Email"
                    control={control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Input placeholder="Email" type="email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <InputFieldContainer
                    label="Phone Number"
                    control={control}
                    name="phone_number"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Input
                            placeholder="Phone Number"
                            type="tel"
                            {...field}
                            value={field.value || ''}
                            className={isDemoMode ? 'blur-sm select-none' : ''}
                            readOnly={isDemoMode}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <InputFieldContainer
                    label="Country"
                    control={control}
                    name="country"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Input
                            placeholder="Country"
                            type="text"
                            {...field}
                            value={field.value || ''}
                            className={isDemoMode ? 'blur-sm select-none' : ''}
                            readOnly={isDemoMode}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <InputFieldContainer
                    label="Company Code"
                    control={control}
                    name="b2b_company_code"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Input
                            placeholder="Company Code"
                            type="text"
                            {...field}
                            className={isDemoMode ? 'blur-sm select-none' : ''}
                            readOnly={isDemoMode}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <InputFieldContainer
                    label="VAT Code"
                    control={control}
                    name="b2b_company_vat_code"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Input
                            placeholder="VAT Code"
                            type="text"
                            {...field}
                            className={isDemoMode ? 'blur-sm select-none' : ''}
                            readOnly={isDemoMode}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <InputFieldContainer
                    label="Website"
                    control={control}
                    name="b2b_company_website"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Input
                            placeholder="https://example.com"
                            type="url"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Separator />
                  {id && <UnifiedCustomerAccountTable customerId={id} />}

                  <h2 className="font-bold tracking-tight">
                    Delivery Addresses
                  </h2>
                  <DataTableManual
                    columns={
                      [
                        {
                          header: 'Description',
                          accessorKey: 'description',
                          cell: ({ row }) => (
                            <div className={isDemoMode ? 'blur-sm select-none' : ''}>
                              {row.getValue('description')}
                            </div>
                          ),
                        },
                        {
                          header: 'Address',
                          accessorKey: 'address_1',
                          cell: ({ row }) => {
                            const address = row.original as any
                            return (
                              <div className={`whitespace-pre-line ${isDemoMode ? 'blur-sm select-none' : ''}`}>
                                {[
                                  address.address_1,
                                  `${address.postal_code} ${address.city}`,
                                  address.country,
                                ]
                                  .filter(Boolean)
                                  .join('\n')}
                              </div>
                            )
                          },
                        },
                        {
                          header: 'Roles',
                          accessorKey: 'roles',
                        },
                      ] as const
                    }
                    data={customerData?.addresses || []}
                    rowCount={customerData?.addresses?.length || 0}
                    pageIndex={0}
                    pageSize={10}
                    onPaginationChange={() => {}}
                    onSortingChange={() => {}}
                    onColumnFiltersChange={() => {}}
                    tableTextSize="text-xs"
                  />

                  <InputFieldContainer
                    label="Components"
                    control={control}
                    name="additional_components"
                    description="Limit component options for the customers"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <AdditionalComponentConstraints field={field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <InputFieldContainer
                    label="Customer Group"
                    control={control}
                    name="customer_groupId"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <CustomerGroupSelect field={field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <InputFieldContainer
                    label="Price List"
                    control={control}
                    name="price_listId"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <PriceListSelect field={field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <InputFieldContainer
                    label="Fabric Palettes"
                    description="Limit fabric palettes for the customers"
                    control={control}
                    name="fabric_palettes"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <PaletteSelect field={field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <InputFieldContainer
                    label="Tags"
                    description="Limit products for the customers"
                    control={control}
                    name="tags"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <TagsSelect field={field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator />

                  <InputFieldContainer
                    label="Configurator"
                    control={control}
                    name="is_configurator_enabled"
                    render={({ field }) => (
                      <FormItem className="col-span-3 flex items-center gap-2 min-h-10">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                        <div className="text-xs text-gray-500 !mt-0">
                          Enable configurator access for this customer.
                        </div>
                      </FormItem>
                    )}
                  />

                  <InputFieldContainer
                    label="Claims"
                    control={control}
                    name="is_claims_enabled"
                    render={({ field }) => (
                      <FormItem className="col-span-3 flex items-center gap-2 min-h-10">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                        <div className="text-xs text-gray-500 !mt-0">
                          Enable claims portal for this customer
                        </div>
                      </FormItem>
                    )}
                  />

                  <div className="flex gap-2">
                    {id && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setShowDeleteDialog(true)}
                        disabled={deleteLoading}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                    <Button
                      type="submit"
                      className="flex-1"
                      disabled={loadingData || updateLoading || createLoading}
                    >
                      {updateLoading || createLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          {id ? 'Updating...' : 'Creating...'}
                        </div>
                      ) : id ? (
                        'Update'
                      ) : (
                        'Create'
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </Form>
        </SheetScrollableContent>
      </SheetContent>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Customer</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this customer? This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
